import clsx from 'clsx';
import { useSetAtom } from 'jotai';
import { type ReactNode, useCallback, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import { subscribeModalOpenAtom } from '@/components/Modals/Subscribe/atoms';
import { getMaxSafelistedChannels } from '@/components/TopBar/SafelistYourChannel/getMaxSafelistedChannels';
import { useHasPermission, useHasRole, useSubscriptionType } from '@/hooks/auth';
import { useSettings } from '@/hooks/state/useSettings';
import { ChannelSafelist } from './ChannelWhitelist';
import { ChannelSafelistInner } from './ChannelWhitelistInner';
import { useRemoveChannel } from './hooks/useRemoveChannel';
import { useWhitelistChannel } from './hooks/useWhitelistChannel';
import { useWhitelistedChannels } from './hooks/useWhitelistedChannels';
import { useOverduePayment } from '@/hooks/billing/useOverduePayment';
import type { YouTubeChannelRequest, YouTubeChannelSnippet } from './types';
const ClickAway = ({
  children,
  onClickAway
}: {
  children: ReactNode;
  onClickAway: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useClickAway(ref, onClickAway);
  return <div ref={ref} data-sentry-component="ClickAway" data-sentry-source-file="index.tsx">{children}</div>;
};

/** Free users only ever see a single (locked) slot. */
const FREE_SLOT_COUNT = 1;
const getChannelsToShow = ({
  hasNoChannelsAndNoPermission,
  channelListLength,
  channelsForAccount,
  maxChannels,
  hideAddSlot
}: {
  hasNoChannelsAndNoPermission: boolean;
  channelListLength: number;
  channelsForAccount: number;
  maxChannels?: number;
  hideAddSlot?: boolean;
}) => {
  if (hasNoChannelsAndNoPermission) {
    return FREE_SLOT_COUNT;
  }
  const showAddSlot = !hideAddSlot && channelListLength < channelsForAccount;
  const baseCount = channelListLength + (showAddSlot ? 1 : 0);
  return Math.min(baseCount, maxChannels ?? channelsForAccount);
};
interface UBSafelistProps {
  compact?: boolean;
  fullWidth?: boolean;
  hideTrashIcon?: boolean;
  maxChannels?: number;
  onUpgradeNeeded?: () => void;
  relativePosition?: boolean;
}
export const UBSafelist: React.FC<UBSafelistProps> = ({
  maxChannels,
  fullWidth,
  relativePosition,
  compact,
  hideTrashIcon,
  onUpgradeNeeded
}) => {
  const [editingIndex, setEditingIndex] = useState<number | undefined>(undefined);
  const [duplicateError, setDuplicateError] = useState<string | undefined>(undefined);
  const isSubmittingRef = useRef(false);
  const {
    settings,
    isLoading: isSettingsLoading
  } = useSettings();
  const isBusiness = !!settings?.isBusiness;
  const setSubscribeModalOpen = useSetAtom(subscribeModalOpenAtom);
  const isFree = useHasRole('Free');
  const subscriptionType = useSubscriptionType();
  const isPaused = !!settings?.subscriptionData?.isPaused;

  // One source of truth for the per-plan channel limit — covers Business too.
  const channelsForAccount = isFree ? FREE_SLOT_COUNT : getMaxSafelistedChannels({
    subscriptionType
  });
  const {
    data,
    isLoading,
    isError: hasFetchError
  } = useWhitelistedChannels();
  const {
    removeChannel,
    isRemoving,
    error: removeChannelError
  } = useRemoveChannel();
  const {
    whitelistChannel,
    isWhitelisting,
    error: whitelistChannelError
  } = useWhitelistChannel();

  // An errored fetch otherwise renders exactly like an empty safelist
  const fetchError = hasFetchError ? "We couldn't load your safelisted channels. Please refresh the page." : undefined;
  const error = duplicateError ?? removeChannelError ?? whitelistChannelError ?? fetchError;
  const channelList = data ?? [];
  const {
    overduePayment
  } = useOverduePayment();
  const handleRemoveChannel = useCallback(async (whitelistId: number) => {
    await removeChannel(whitelistId);
  }, [removeChannel]);
  const handleSafelistChannel = async (channelData: YouTubeChannelRequest | YouTubeChannelSnippet) => {
    setEditingIndex(undefined);
    setDuplicateError(undefined);
    if (!channelData) {
      return;
    }

    // Ignore a submit while one is already in flight
    if (isSubmittingRef.current || isWhitelisting) {
      return;
    }
    const isAlreadySafelisted = channelList.some(channel => channel.channelId === channelData.channelId);
    if (isAlreadySafelisted) {
      setDuplicateError('This channel is already on your safelist.');
      return;
    }
    isSubmittingRef.current = true;
    try {
      await whitelistChannel(channelData);
    } finally {
      isSubmittingRef.current = false;
    }
  };
  const hasSafelistPermission = useHasPermission('safelist_yt');

  // If user has no safelisted channels and no permission, only show 1 slot
  const hasNoChannelsAndNoPermission = channelList.length === 0 && !hasSafelistPermission;

  // Recalculate channelsToShow based on permission
  const channelsToShow = getChannelsToShow({
    hasNoChannelsAndNoPermission,
    channelListLength: channelList.length,
    channelsForAccount,
    maxChannels,
    hideAddSlot: isPaused
  });

  // Safelisting is disabled for overdue payments and paused subscriptions
  const isSafelistingDisabled = !!overduePayment || isPaused;
  const handleChannelClick = () => {
    if (isFree) {
      // For free users, always open the subscribe modal
      setSubscribeModalOpen(true);
      return;
    }
    if (!hasSafelistPermission && onUpgradeNeeded) {
      // For other users without safelist permission, call the provided callback
      onUpgradeNeeded();
      return;
    }
  };

  // Determine layout based on number of channels and layout preference
  const layoutClasses = fullWidth ? 'flex flex-col' : 'flex flex-col @xl:flex-row @xl:flex-wrap';
  return <>
      <ul className={clsx('my-5 list-none gap-6', layoutClasses, 'ub-account-page_whitelisting', isBusiness && 'business', !!compact && 'my-0! gap-2!', !!(isFree || isSafelistingDisabled) && 'pointer-events-none!')}>
        {[...new Array(channelsToShow)].map((_channel, i) => {
        const isEditing = editingIndex === i;
        const currentChannel = channelList[i] ?? {};
        const hasChannel = !!currentChannel.channelId && !!currentChannel.channelName;
        const slotKey = currentChannel.id ? `whitelist-${currentChannel.id}` : `slot-${i}`;
        return <li className="w-full @md:max-w-[322px] @xl:max-w-[388px]" key={slotKey}>
              <ClickAway onClickAway={() => {
            if (isEditing) {
              setEditingIndex(undefined);
            }
            setDuplicateError(undefined);
          }}>
                <div className={clsx('pointer-events-auto relative flex h-20 w-full items-center justify-center rounded-xl border-1 border-gray-300 border-dashed bg-transparent dark:border-gray-800 dark:bg-transparent', 'hover:bg-gray-100 dark:hover:bg-gray-900', !!relativePosition && 'h-full min-h-20', (!!hasChannel || !!isEditing) && ['active items-start border-none', 'hover:bg-gray-100 dark:hover:bg-gray-900'], !!compact && 'min-h-16!', (!!hasNoChannelsAndNoPermission || isPaused) && 'opacity-60')}>
                  <ChannelSafelistInner disabled={isSafelistingDisabled} handleEdit={() => {
                // Disable editing if user has overdue payment
                if (isSafelistingDisabled) {
                  return;
                }
                if (hasSafelistPermission) {
                  if (!hasChannel) {
                    setDuplicateError(undefined);
                    setEditingIndex(i);
                  }
                } else {
                  handleChannelClick();
                }
              }} hasChannel={hasChannel} isEditing={isEditing} isFetching={isLoading || isWhitelisting || isRemoving} isLoading={isLoading || isSettingsLoading} {...!!hasNoChannelsAndNoPermission && {
                tooltipLabel: 'Subscribe to add a YouTube channel to your safelist'
              }}>
                    <ChannelSafelist compact={compact} disabled={isSafelistingDisabled} handleRemoveChannel={handleRemoveChannel} hideTrashIcon={hideTrashIcon} isEditing={isEditing} isFetching={isLoading || isWhitelisting || isRemoving} isRemoving={isRemoving} key={slotKey} selectedChannel={currentChannel?.channelId} whitelistChannel={handleSafelistChannel} whitelistId={currentChannel?.id} isPaused={isPaused} />
                  </ChannelSafelistInner>
                </div>
              </ClickAway>
            </li>;
      })}
      </ul>

      {error !== undefined && <div className="mt-4 rounded-xs bg-[#f34949] px-6 py-2 text-center font-sans text-sm text-white">
          {error}
        </div>}
    </>;
};